/**
 * CLE-2811 — what the run page shows for a run's state: whether the conversation is mounted, whether the
 * pre-generation Generate row is, what a failed pass means in plain words, what Retry does, and when the
 * composer is closed. Pure, so each rule is tested without a DOM.
 */

import {
  DraftRunStatus,
  IDraftRunDetail,
} from "store/queries/contractModelDrafts";

import {
  COMPOSER_CLOSED_CANCELED,
  COMPOSER_CLOSED_CANCELED_FROM_CONTRACT,
  COMPOSER_CLOSED_DEAD,
  RUN_ERROR_AI_CRASHED,
  RUN_ERROR_BUDGET_PASSES,
  RUN_ERROR_EXTRACTION,
  RUN_ERROR_LEASE,
  RUN_ERROR_NO_RESULT,
  RUN_ERROR_UNKNOWN,
  runErrorBudget,
  runErrorDownload,
} from "./copy";
import { isProcessing } from "./utils";

/** A pass that ended without a result: `failed` (it stopped) or `dead` (retried until out of attempts). */
export const isFailedRun = (status: DraftRunStatus): boolean =>
  status === "failed" || status === "dead";

type RunShape = Pick<IDraftRunDetail, "status" | "recommendations">;

/**
 * The conversation (and the wizard beside it) is mounted while a pass runs, once there is a model to talk
 * about — and for a failed or dead pass even with nothing modeled, because the conversation is where the
 * failure and its Retry are shown. It used to disappear on a failed first pass, taking the reason with it.
 */
export const showsConversation = (run: RunShape): boolean =>
  run.recommendations.length > 0 ||
  isProcessing(run.status) ||
  isFailedRun(run.status);

/**
 * The upload-then-Generate row (with the pre-generation instructions box) belongs to a run that has not
 * produced anything and is not being worked on: a `collecting` run, or a pass that finished with nothing.
 * A contract run whose documents came from the contract now arrives `queued` — the server starts it — so
 * it never shows this row; neither does a run with a model (Regenerate lives in the conversation), a
 * failed or dead one (Retry does) or a canceled one (the server refuses to generate it).
 */
export const showsGenerateRow = (run: RunShape): boolean =>
  !isProcessing(run.status) &&
  run.recommendations.length === 0 &&
  !isFailedRun(run.status) &&
  run.status !== "canceled";

export type RetryMode = "regenerate" | "generate";

/**
 * What Retry calls. `regenerate` makes a new version and diffs it against the model she is reviewing —
 * but the server refuses it (409) unless the latest version has recommendations. So a failed FIRST pass,
 * or a failed regeneration whose empty version is still the latest, is retried with `generate`, which
 * re-runs the pass into the latest version.
 */
export const retryModeOf = (
  run: Pick<RunShape, "recommendations">
): RetryMode => (run.recommendations.length > 0 ? "regenerate" : "generate");

/** Retry is offered on a failed or dead pass that still has documents to read (generate needs one). */
export const canRetryRun = (
  run: Pick<IDraftRunDetail, "status" | "documents">
): boolean => isFailedRun(run.status) && run.documents.length > 0;

/**
 * Why the composer is closed, or null when it is open. A canceled run refuses every message (409); a dead
 * one would only queue a message no pass will ever pick up — Retry is the way on.
 */
export const composerClosedReason = (
  run: Pick<IDraftRunDetail, "status" | "contractFeeScheduleId">
): string | null => {
  if (run.status === "canceled")
    return run.contractFeeScheduleId
      ? COMPOSER_CLOSED_CANCELED_FROM_CONTRACT
      : COMPOSER_CLOSED_CANCELED;
  if (run.status === "dead") return COMPOSER_CLOSED_DEAD;
  return null;
};

export interface IRunErrorText {
  /** One sentence in plain words. */
  summary: string;
  /** The raw message the worker or clear-api wrote, for "Details"; null when there is none. */
  details: string | null;
}

/**
 * `claude ran past the <budget> budget of <duration> (<ENV_KNOB>) without writing a valid result.json — …`
 * (`budgetFailureMessage` in agents/scripts/contract-model-worker.mjs). The budget is the stage (`facts`,
 * `full`) or the facts pipeline's fallback (`facts fallback`); the duration is "6 min" or "330 s".
 */
const BUDGET = /ran past the (.+?) budget of (.+?) \(/;
/** `download failed for <fileName> (<s3Key>): …` (the worker's download step). */
const DOWNLOAD = /^download failed for (.+?) \(/;

/**
 * A failed pass's `lastError`, in plain words. The worker's messages are written for the logs — env var
 * names, token counts, "result.json" — so each known shape maps to one sentence and the raw text stays
 * under "Details" for whoever debugs it. Anything unrecognised gets a generic sentence, never nothing.
 */
export const humanizeRunError = (
  lastError: string | null | undefined
): IRunErrorText => {
  const raw = typeof lastError === "string" ? lastError.trim() : "";
  const details = raw || null;
  const budget = raw.match(BUDGET);
  if (budget) {
    const name = budget[1].trim();
    return {
      summary: runErrorBudget(
        budget[2].trim(),
        RUN_ERROR_BUDGET_PASSES[name] ?? `the ${name} pass`
      ),
      details,
    };
  }
  if (/^worker lease expired/i.test(raw))
    return { summary: RUN_ERROR_LEASE, details };
  const download = raw.match(DOWNLOAD);
  if (download) return { summary: runErrorDownload(download[1]), details };
  if (/^document extraction failed/i.test(raw))
    return { summary: RUN_ERROR_EXTRACTION, details };
  if (/^claude -p failed/i.test(raw))
    return { summary: RUN_ERROR_AI_CRASHED, details };
  if (/^no valid result\.json/i.test(raw))
    return { summary: RUN_ERROR_NO_RESULT, details };
  return { summary: RUN_ERROR_UNKNOWN, details };
};
